/* eslint-disable wrap-iife */
import { UploadStatusTip, useImageRecognition, useVideoUpload } from '@/hooks/useImageRecognition';
import PicSmallerUpload from '@/pages/combinedWard/picSmaller/components/PicSmallerUpload';
import CompressImage from '@/pages/patient/patientOcrImage/components/CompressImage';
import { UploadVideoStatusTip } from '@/pages/patient/patientOcrImage/hook/useFile';
import classNames from 'classnames';
import { forwardRef, useContext, useEffect, useImperativeHandle, useRef } from 'react';
import { OcrContext } from '../PatientTabs/components/TabItem';
import styles from './index.less';

interface SingleCompressProps {
  accept?: string;
  recordId?: string;
  label?: string;
  fileType?: string;
  patientId?: string;
  duration?: number; // 视频时长
  currentIndex?: any; // 当前图片的索引
  onUpdate?: (url?: string) => void;
  onCanOcr?: (bol: boolean) => void;
  width?: number; // 宽度
  height?: number; // 高度
  fontSize?: number; // 字体大小
}

/**
 * @description: 当个压缩上传
 * @author: KaifengLi
 * @version: v2.27.0
 * @Date: 2024-11-21 10:34:42
 */
const SingleCompress = forwardRef((props: SingleCompressProps, ref) => {
  const {
    accept,
    recordId,
    label,
    currentIndex,
    fileType,
    duration,
    onUpdate,
    onCanOcr,
    patientId,
    width,
    height,
    fontSize,
  } = props;

  const { setRecognitTaskId } = useContext(OcrContext);

  useImperativeHandle(ref, () => ({
    loading: () =>
      saveImageLoading ||
      videoUploading ||
      [...processingImageFiles, ...processingVideoFiles].length > 0 ||
      patientImageTasks?.length > 0,
  }));

  const { videoUploading, setVideoUploadFiles, processingVideoFiles } = useVideoUpload(
    (urls?: string[]) => {
      console.log({ urls });
      if (urls) {
        updateCallBack?.current?.(urls);
      }
    },
  );

  const {
    processingImageFiles,
    patientImageTasks,
    recognitTaskId,
    saveImageLoading,
    setUploadImageFiles,
  } = useImageRecognition({
    scene: 'single',
    fileType,
    recordId,
    patientId,
    callback: (urls: string[]) => {
      // console.log(urls);
      if (urls) {
        updateCallBack?.current?.(urls);
      }
    },
    canOcr: (bol: boolean) => {
      onCanOcr?.(bol);
    },
  });

  // 使用 useRef 来存储定时器和任务 ID 列表
  const updateCallBack = useRef<any>(null);

  useEffect(() => {
    updateCallBack.current = onUpdate;
  }, [onUpdate]);

  useEffect(() => {
    if (recognitTaskId) {
      setRecognitTaskId?.(recognitTaskId);
    }
  }, [recognitTaskId]);

  return (
    <div data-no-drag="true" className={classNames('filtered', styles['single-compress'])}>
      <PicSmallerUpload
        auto={false}
        accept={accept}
        multiple
        duration={duration}
        label={label}
        border="solid"
        width={width}
        height={height}
        fontSize={fontSize}
        // loading={saveImageLoading || videoUploading}
        uploadTip="图片压缩中"
        onChange={(urls, file, fileLen?: number) => {
          if (urls.length) {
            console.log(urls);
            updateCallBack?.current?.(urls);
          }
          if (file.length) {
            if (file.some((el) => el.type === 'video')) {
              setVideoUploadFiles((prev) => {
                const olds = prev.map((el) => {
                  return {
                    ...el,
                    new: false,
                  };
                });
                return [...olds, ...file.filter((el) => el.type === 'video')];
              });
            }
            if (file.some((el) => el.type === 'image')) {
              setUploadImageFiles((prev) => {
                const olds = prev.map((el) => {
                  return {
                    ...el,
                    new: false,
                  };
                });
                return [...olds, ...file.filter((el) => el.type === 'image')];
              });
            }
          }
        }}
        onVideoUpload={(file) => {
          console.log('视频上传回调', file);
        }}
      />
      {patientImageTasks?.map((el, index) => (
        <CompressImage
          key={el?.id}
          progress={el?.progress}
          index={index}
          loading
          deleted={false}
          width={width}
          height={height}
          edit={false}
          blob={!!el?.compress}
          tip="身份脱敏中..."
          picSource={patientImageTasks?.map((item) => item?.beforeFilePath) as string[]}
          url={el?.beforeFilePath}
        />
      ))}
      {[...processingImageFiles, ...processingVideoFiles]?.map((el, index) => (
        <CompressImage
          key={el.id}
          progress={-1}
          index={index}
          loading
          edit={false}
          deleted={false}
          width={width}
          height={height}
          unit={el?.file?.type?.includes('image') ? 'image' : 'video'}
          picSource={
            [...processingImageFiles, ...processingVideoFiles]?.map(
              (item) => item?.compress?.src,
            ) as string[]
          }
          blob={!!el?.compress}
          url={el?.compress?.src}
          tip={
            el?.file?.type?.includes('image')
              ? UploadStatusTip[el?.uploadStatus || 'waiting']
              : UploadVideoStatusTip[el?.uploadStatus || 'waiting']
          }
          // onDelete={() => removeFileFromQueue(index)}
        />
      ))}
    </div>
  );
});

export default SingleCompress;
